/**
 * Facebook pixel SDK: loading, lifecycle protection, and initialization.
 *
 * Handles: dynamically loading the SDK script, initializing with user data,
 * suppressing auto-PageViews, and restoring pushState wrappers after the SDK
 * hijacks them.
 */

import { state, debugLog } from "./state.js";
import { CONFLICT_FOREIGN_CONVERSION, isForeignConversion } from "./pixel-conflict.js";
import { getExternalId } from "./cookies.js";
import {
  FB_USER_DATA_KEYS,
  EVENT_ID_PREFIX,
} from "./constants.js";
import { onUrlChange } from "./spa-navigation.js";
import { syncCanonicalUrl } from "./facebook-pixel.js";

// --- History API wrapper (FB SDK re-wrapping) ---

/**
 * Apply our wrapper around a History API method (pushState or replaceState).
 *
 * The wrapper calls the original method and then fires our SPA URL-change handler.
 * Isolates the `as unknown as` cast needed because TypeScript doesn't allow
 * the `arguments` object to be directly typed as a tuple.
 */
function applyHistoryWrapper(
  methodName: "pushState" | "replaceState",
  original: History["pushState"] | History["replaceState"],
): void {
  history[methodName] = function () {
    original.apply(history, arguments as unknown as Parameters<History["pushState"]>);
    onUrlChange();
  };
}

/**
 * Re-apply our pushState/replaceState wrappers after the FB SDK installs its own.
 *
 * The FB SDK wraps pushState and replaceState to auto-fire PageView events on every
 * SPA navigation. This causes double-counting because we already handle SPA navigations
 * ourselves via onUrlChange(). By re-wrapping with our originals, we bypass the SDK's
 * auto-PageView behavior while keeping explicit fbq('track') calls working normally.
 */
function restorePushStateWrappers(): void {
  if (state.pushStateRestored) return;
  if (!state.originalPushState || !state.originalReplaceState) return;
  state.pushStateRestored = true;

  applyHistoryWrapper("pushState", state.originalPushState);
  applyHistoryWrapper("replaceState", state.originalReplaceState);
}

// --- SDK lifecycle ---

/**
 * Suppress SDK auto-PageViews by intercepting fbq.callMethod.
 *
 * The FB SDK fires PageView events automatically in several scenarios:
 * - On initial fbq('init') if autoConfig is enabled
 * - On pushState/replaceState (via its History API hooks)
 * - Via MutationObserver on DOM changes (microdata-based)
 *
 * These auto-PageViews cause double-counting because we fire our own PageView
 * events with dedup event IDs (prefixed "tst_"). This filter only allows
 * PageView events with our tst_ eventID prefix through, blocking all others.
 */
function suppressAutoPageViews(): void {
  if (state.autoPageViewsSuppressed) return;
  if (!window.fbq || !window.fbq.callMethod) return;
  state.autoPageViewsSuppressed = true;

  const original = window.fbq.callMethod.bind(window.fbq);
  window.fbq.callMethod = function () {
    const args = Array.prototype.slice.call(arguments);
    // Block auto-PageViews (no eventID or non-tst_ eventID)
    if (args[0] === "track" && args[1] === "PageView") {
      const options = args[3] as { eventID?: string } | undefined;
      if (!options || !options.eventID || !String(options.eventID).startsWith(EVENT_ID_PREFIX)) {
        return;
      }
    }
    // trackSingle has different signature: ("trackSingle", pixelId, eventName, params, options)
    if (args[0] === "trackSingle" && args[2] === "PageView") {
      const options = args[4] as { eventID?: string } | undefined;
      if (!options || !options.eventID || !String(options.eventID).startsWith(EVENT_ID_PREFIX)) {
        return;
      }
    }
    return original.apply(null, args);
  };
}

/** Consolidated SDK-ready callback: runs all SDK-dependent setup. */
function onFbSdkReady(): void {
  if (state.fbSdkReadyFired) return;
  state.fbSdkReadyFired = true;
  restorePushStateWrappers();
  suppressAutoPageViews();
}

/**
 * Resolve the SDK load: call onFbSdkReady if the SDK loaded successfully,
 * then invoke the sdkResolvedCallback so event firing can proceed.
 */
function resolveSdk(ready: boolean): void {
  debugLog("resolveSdk", "ready=" + ready, "href=" + window.location.href);
  state.sdkResolved = true;
  if (ready) {
    onFbSdkReady();
  }
  if (state.sdkResolvedCallback) {
    var cb = state.sdkResolvedCallback;
    state.sdkResolvedCallback = null;
    cb();
  }
}

// --- Loading ---

/**
 * Remember that somebody else put a conversion on our pixel.
 *
 * Once per page is enough: the merchant needs to know it happens, not how often
 * within a single page view. Wrapped in a try/catch because this sits in the
 * hot path of every fbq call on the shop, and a detector that breaks the shop's
 * own tracking is worse than the problem it reports.
 */
function noteForeignConversion(args: readonly unknown[]): void {
  try {
    if (state.pixelConflicts.indexOf(CONFLICT_FOREIGN_CONVERSION) !== -1) return;
    if (!state.config) return;
    if (!isForeignConversion(args, EVENT_ID_PREFIX, String(state.config.pixelId))) return;
    state.pixelConflicts.push(CONFLICT_FOREIGN_CONVERSION);
    debugLog("pixelConflict", "another tool sent a conversion to our pixel", args[0], args[1]);
  } catch {
    // Deliberately silent. Never let a diagnostic take the shop down.
  }
}

/**
 * Wrap somebody else's fbq so we can see conversions land on our pixel.
 *
 * Observation only: the call is passed through untouched, and a failure here
 * must never take the shop's own tracking with it. Installed at most once,
 * because fbq's callMethod is reassigned by the SDK and wrapping a wrapper
 * every time would grow a chain.
 */
function observeForeignConversions(): void {
  try {
    if (state.foreignConversionWatchInstalled) return;
    const fbq = window.fbq;
    if (!fbq || typeof fbq.callMethod !== "function") return;

    const original = fbq.callMethod;
    fbq.callMethod = function (this: unknown) {
      const args = Array.prototype.slice.call(arguments);
      noteForeignConversion(args);
      return original.apply(this, Array.from(arguments) as []);
    } as typeof fbq.callMethod;

    state.foreignConversionWatchInstalled = true;
    debugLog("pixelConflict", "watching a pre-existing fbq");
  } catch {
    // Deliberately silent. A diagnostic may never break the shop.
  }
}

/** Build init data from user data for fbq('init'). */
function buildInitData(): Record<string, string> {
  const initData: Record<string, string> = { external_id: getExternalId() };
  for (const fieldKey of FB_USER_DATA_KEYS) {
    if (state.userData[fieldKey]) initData[fieldKey] = state.userData[fieldKey];
  }
  return initData;
}

/**
 * Load Facebook pixel SDK dynamically.
 *
 * @param pixelId  The Facebook pixel ID to initialize
 * @param onResolved  Optional callback invoked once SDK status is known (loaded or failed).
 *                    Events should only be fired after this callback runs, ensuring
 *                    browserPixelFired is accurate in the CAPI payload.
 */
export function loadFacebookPixel(pixelId: string, onResolved?: () => void): void {
  debugLog("loadFbPixel", "pixelId=" + pixelId, "href=" + window.location.href);
  if (state.initializedPixels[pixelId]) {
    if (onResolved) onResolved();
    return;
  }
  state.initializedPixels[pixelId] = true;

  // Sync canonical before FB SDK loads, because the SDK reads it for the dl parameter
  syncCanonicalUrl();

  if (window.fbq) {
    if (!state.fbqIsOurs) {
      console.warn("[TheSocialTap] Pre-existing Facebook pixel detected. Using trackSingle for isolation.");
    }

    const initAndProceed = () => {
      const initData = buildInitData();
      window.fbq!("consent", "grant");
      window.fbq!("set", "autoConfig", false, pixelId);
      window.fbq!("init", pixelId, initData);
      // Watch this fbq too. The trap that spots a second tool putting
      // conversions on our pixel is installed where we CREATE fbq, and that
      // branch never runs when the other plugin loaded first. Which is exactly
      // the shop that has the problem, so leaving it out would mean the
      // detector works everywhere except where it is needed.
      observeForeignConversions();
      state.sdkResolved = true;
      onFbSdkReady();
      if (onResolved) onResolved();
    };

    // SDK fully loaded, proceed immediately
    if (window.fbq.callMethod) {
      initAndProceed();
      return;
    }

    // SDK stub exists but not fully loaded, poll for callMethod
    debugLog("loadFbPixel", "pre-existing stub detected, waiting for callMethod...");
    const POLL_INTERVAL = 50;
    const POLL_TIMEOUT = 5000;
    let elapsed = 0;
    const checkInterval = setInterval(() => {
      elapsed += POLL_INTERVAL;
      if (window.fbq && window.fbq.callMethod) {
        clearInterval(checkInterval);
        initAndProceed();
      } else if (elapsed >= POLL_TIMEOUT) {
        clearInterval(checkInterval);
        debugLog("loadFbPixel", "callMethod timeout, proceeding without browser pixel");
        // Still resolve so CAPI events fire, but browser pixel won't work
        state.sdkResolved = true;
        if (onResolved) onResolved();
      }
    }, POLL_INTERVAL);
    return;
  }

  // Store callback for when SDK status is resolved
  if (onResolved) state.sdkResolvedCallback = onResolved;

  // Facebook pixel base code (standard snippet)
  const n = function () {
    if (n.callMethod) {
      n.callMethod.apply(n, arguments as unknown as unknown[]);
    } else {
      n.queue!.push(arguments);
    }
  } as FbqFunction & FbqExtensions;
  n.push = n as unknown as (...args: unknown[]) => void;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  state.fbqIsOurs = true;

  // Trap callMethod assignment so our PageView filter is active from the
  // very first call, before the SDK can fire auto-PageViews during init.
  //
  // Timeline without trap:
  //   T1: SDK script executes, sets fbq.callMethod = sdkFunction
  //   T2: SDK processes queue, fires auto-PageView (no tst_ eventID, wrong URL)
  //   T3: "load" event fires, suppressAutoPageViews() installs, TOO LATE
  //
  // With trap:
  //   T1: SDK sets fbq.callMethod, our set() trap fires and wraps with the filter
  //   T2: SDK processes queue, auto-PageView goes through the filter and is BLOCKED
  //   T3: Our events with tst_ eventID pass through normally
  let _callMethod: ((...args: unknown[]) => void) | undefined;
  Object.defineProperty(n, "callMethod", {
    configurable: true,
    enumerable: true,
    get() { return _callMethod; },
    set(fn: (...args: unknown[]) => void) {
      debugLog("callMethodTrap", "SDK set callMethod, wrapping with PageView filter");
      _callMethod = function () {
        const args = Array.prototype.slice.call(arguments);
        // Block PageView without our tst_ eventID prefix
        if (args[0] === "track" && args[1] === "PageView") {
          const options = args[3] as { eventID?: string } | undefined;
          if (!options || !options.eventID || !String(options.eventID).startsWith(EVENT_ID_PREFIX)) {
            debugLog("callMethodTrap", "BLOCKED auto-PageView (track)", "eventID=" + (options && options.eventID));
            return;
          }
        }
        if (args[0] === "trackSingle" && args[2] === "PageView") {
          const options = args[4] as { eventID?: string } | undefined;
          if (!options || !options.eventID || !String(options.eventID).startsWith(EVENT_ID_PREFIX)) {
            debugLog("callMethodTrap", "BLOCKED auto-PageView (trackSingle)", "eventID=" + (options && options.eventID));
            return;
          }
        }
        // Watch, never block. A conversion from another tool is that tool's to
        // send; swallowing it would break their reporting to fix ours. We only
        // need to know it happened, so the merchant can be told.
        noteForeignConversion(args);
        // Array.from: `arguments` is een IArguments, geen array. Werkte bij
        // .apply prima maar de typecheck van dit script stond uit, dus dit is
        // nooit opgevallen.
        return fn.apply(null, Array.from(arguments));
      };
      state.autoPageViewsSuppressed = true;
    },
  });

  const fbScriptEl = document.createElement("script");
  fbScriptEl.async = true;
  fbScriptEl.src = "https://connect.facebook.net/en_US/fbevents.js";

  // Attach listeners BEFORE DOM insertion, which guarantees events are not missed
  // even when the script is served from browser cache.
  fbScriptEl.addEventListener("load", function () {
    if (window.fbq && window.fbq.callMethod) {
      resolveSdk(true);
    } else {
      // SDK script loaded but callMethod not yet set, so give it one event-loop tick.
      // This is NOT a timing-based timeout; it yields to the microtask queue so the
      // SDK's synchronous init code can finish before we check.
      setTimeout(function () {
        resolveSdk(!!(window.fbq && window.fbq.callMethod));
      }, 0);
    }
  });

  fbScriptEl.addEventListener("error", function () {
    resolveSdk(false);
  });

  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode!.insertBefore(fbScriptEl, firstScript);

  // Override FB SDK's built-in Google Consent Mode check, because our own consent
  // logic in sendEvent() is the single source of truth for pixel firing.
  window.fbq!("consent", "grant");

  // Init pixel with external_id and any existing user data
  const initData = buildInitData();
  window.fbq!("set", "autoConfig", false, pixelId);
  window.fbq!("init", pixelId, initData);
}
